import './App.css';
import { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Overlay from './Overlay';

// Badge type to SVG filename mapping (for Kick badges)
const badgeMapping = {
  broadcaster: 'broadcaster',
  founder: 'founder',
  moderator: 'moderator',
  og: 'og',
  staff: 'staff',
  subscriber: 'subscriber',
  verified: 'verified',
  vip: 'vip',
  sub_gifter: 'subgifter'
};

// Twitch badge type to UUID mapping
const twitchBadgeUUIDs = {
  // Global badges
  staff: 'd97c37bd-a6f5-4c38-8f57-4e4bef88af34',
  partner: 'd12a2e27-16f6-41d0-ab77-b780518f00a3',
  premium: 'bbbe0db0-a598-423e-86d0-f9fb98ca1933',
  
  // Channel badges
  broadcaster: '5527c58c-fb7d-422d-b71b-f309dcb85cc1',
  moderator: '3267646d-33f0-4b17-b3df-f923a41db1d0',
  vip: 'b817aba4-fad8-49e2-b88a-7cc744dfa6ec',
  founder: '511b78a9-ab37-472f-9569-457753bbe7d3',
  'artist-badge': '4300a897-03dc-4e83-8c0e-c332fee7057f',
  no_audio: 'aef2cd08-f29b-45a1-8c12-d44d7fd5e6f0',
  no_video: '199a0dba-58f3-494e-a7fc-1fa0a1001fb8',
  
  // Default subscriber badge
  subscriber: '5d9f2208-5dd8-11e7-8513-2ff4adfae661',
  
  // Sub-gifter badges (version corresponds to sub count)
  'sub-gifter': {
    1: 'f1d8486f-eb2e-4553-b44f-4d614617afc1',
    5: '3e638e02-b765-4070-81bd-a73d1ae34965',
    10: 'bffca343-9d7d-49b4-a1ca-90af2c6a1639',
    25: '17e09e26-2528-4a04-9c7f-8518348324d1',
    50: '47308ed4-c979-4f3f-ad20-35a8ab76d85d',
    100: '5056c366-7299-4b3c-a15a-a18573650bfb',
    250: 'df25dded-df81-408e-a2d3-40d48f0d529f',
    500: 'f440decb-7468-4bf9-8666-98ba74f6eab5',
    1000: 'b8c76744-c7e9-44be-90d0-08840a8f6e39'
  },
  
  // Bits badges (version corresponds to bit amount)
  bits: {
    1: '73b5c3fb-24f9-4a82-a852-2f475b59411c',
    100: '09d93036-e7ce-431c-9a9e-7044297133f2',
    1000: '0d85a29e-79ad-4c63-a285-3acd2c66f2ba',
    5000: '57cd97fc-3e9e-4c6d-9d41-60147137234e',
    10000: '68af213b-a771-4124-b6e3-9bb6d98aa732',
    25000: '64ca5920-c663-4bd8-bfb1-751b4caea2dd',
    50000: '62310ba7-9916-4235-9eba-40110d67f85d',
    75000: 'ce491fa4-b24f-4f3b-b6ff-44b080202792',
    100000: '96f0540f-aa63-49e1-a8b3-259ece3bd098',
    200000: '4a0b90c4-e4ef-407f-84fe-36b14aebdbb6',
    300000: 'ac13372d-2e94-41d1-ae11-ecd677f69bb6',
    400000: 'a8f393af-76e6-4aa2-9dd0-7dcc1c34f036',
    500000: 'f6932b57-6a6e-4062-a770-dfbd9f4302e5',
    600000: '4d908059-f91c-4aef-9acb-634434f4c32e',
    700000: 'a1d2a824-f216-4b9f-9642-3de8ed370957',
    800000: '5ec2ee3e-5633-4c2a-8e77-77473fe409e6',
    900000: '088c58c6-7c38-45ba-8f73-63ef24189b84',
    1000000: '494d1c8e-c3b2-4d88-8528-baff57c9bd3f'
  }
};

// Helper function to get Twitch badge URL
const getTwitchBadgeUrl = (badgeType, version) => {
  const badgeUUID = twitchBadgeUUIDs[badgeType];
  
  if (!badgeUUID) {
    // Unknown badge type, return null or fallback
    return null;
  }
  
  // Handle versioned badges (sub-gifter and bits)
  if (typeof badgeUUID === 'object') {
    // For versioned badges, the version is the tier/amount
    // Find the closest tier that matches or is less than the version
    const tiers = Object.keys(badgeUUID)
      .map(Number)
      .sort((a, b) => b - a); // Sort descending
    
    let selectedTier = tiers[tiers.length - 1]; // Default to lowest tier
    for (const tier of tiers) {
      if (version >= tier) {
        selectedTier = tier;
        break;
      }
    }
    
    const uuid = badgeUUID[selectedTier];
    return `https://static-cdn.jtvnw.net/badges/v1/${uuid}/1`;
  }
  
  // For regular badges, version is always 1
  return `https://static-cdn.jtvnw.net/badges/v1/${badgeUUID}/1`;
};

// Custom hook to animate number transitions with exponential easing
function useAnimatedNumber(targetValue, duration = 800) {
  const [animatedValue, setAnimatedValue] = useState(targetValue !== null ? targetValue : 0);
  const animationFrameRef = useRef(null);
  const startTimeRef = useRef(null);
  const startValueRef = useRef(targetValue !== null ? targetValue : 0);

  useEffect(() => {
    if (targetValue === null) {
      setAnimatedValue(0);
      startValueRef.current = 0;
      return;
    }

    // Clear any existing animation
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    const startValue = startValueRef.current;
    const endValue = targetValue;
    const difference = endValue - startValue;
    
    // If values are the same, no animation needed
    if (difference === 0) {
      return;
    }

    // Exponential easing function (ease-out)
    // This makes large numbers animate much faster
    const easeOutExpo = (t) => {
      return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
    };

    const animate = (currentTime) => {
      if (startTimeRef.current === null) {
        startTimeRef.current = currentTime;
      }

      const elapsed = currentTime - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      
      // Apply exponential easing
      const easedProgress = easeOutExpo(progress);
      
      // Calculate new value
      const newValue = Math.round(startValue + (difference * easedProgress));
      
      setAnimatedValue(newValue);
      startValueRef.current = newValue;

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        // Ensure we end exactly at the target
        setAnimatedValue(endValue);
        startValueRef.current = endValue;
        startTimeRef.current = null;
        animationFrameRef.current = null;
      }
    };

    startTimeRef.current = null;
    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [targetValue, duration]);

  return animatedValue;
}

function App() {
  // Simple routing based on pathname
  const [currentPath] = useState(window.location.pathname);
  
  // PIN authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const CORRECT_PIN = '0101';
  
  // All hooks must be called before any conditional returns
  const [messages, setMessages] = useState([]);
  const [balance, setBalance] = useState(0);
  const [inputValue, setInputValue] = useState('');
  const chatEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const kickWsRef = useRef(null);
  const pumpSocketRef = useRef(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const lastKickMessageTimeRef = useRef(null);
  const isReconnectingKickRef = useRef(false);
  const reconnectKickTimeoutRef = useRef(null);
  
  // Viewer counts and bitrate state
  const [kickViewers, setKickViewers] = useState(null);
  const [pumpViewers, setPumpViewers] = useState(null);
  const [twitchViewers, setTwitchViewers] = useState(null);
  const [bitrate, setBitrate] = useState(null);
  const [marketCap, setMarketCap] = useState(null);
  
  // Connection status tracking
  const [kickConnected, setKickConnected] = useState(false);
  const [pumpConnected, setPumpConnected] = useState(false);
  const [twitchConnected, setTwitchConnected] = useState(false);
  
  // Challenge state
  const [challenges, setChallenges] = useState([]);
  const [challengeTriggerAmount, setChallengeTriggerAmount] = useState(0);
  const [currentChallenge, setCurrentChallenge] = useState(null);
  const [showChallengesModal, setShowChallengesModal] = useState(false);
  const [newChallengeText, setNewChallengeText] = useState('');
  const [triggerAmountInput, setTriggerAmountInput] = useState('');
  
  // Image display state
  const [imageDisplay, setImageDisplay] = useState({ imageData: null, text: null, visible: false });
  const [imageFile, setImageFile] = useState(null);
  const [imageDisplayText, setImageDisplayText] = useState('');
  const [imageDisplayWarningDismissed, setImageDisplayWarningDismissed] = useState(false);

  // Image compression function
  const compressImage = (file, maxWidth = 1920, maxHeight = 1920, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      // Set timeout for the entire operation (30 seconds)
      const timeout = setTimeout(() => {
        reject(new Error('Image processing timed out'));
      }, 30000);
      
      const clearTimeoutAndResolve = (result) => {
        clearTimeout(timeout);
        resolve(result);
      };
      
      const clearTimeoutAndReject = (error) => {
        clearTimeout(timeout);
        reject(error);
      };
      
      const reader = new FileReader();
      
      reader.onerror = () => {
        clearTimeoutAndReject(new Error('Failed to read image file'));
      };
      
      reader.onload = (event) => {
        try {
          const img = new Image();
          
          img.onerror = () => {
            clearTimeoutAndReject(new Error('Failed to load image into canvas'));
          };
          
          img.onload = () => {
            try {
              // Check if image dimensions are valid
              if (img.width === 0 || img.height === 0) {
                clearTimeoutAndReject(new Error('Invalid image dimensions'));
                return;
              }
              
              const canvas = document.createElement('canvas');
              let width = img.width;
              let height = img.height;

              // Calculate new dimensions
              if (width > height) {
                if (width > maxWidth) {
                  height = Math.round((height * maxWidth) / width);
                  width = maxWidth;
                }
              } else {
                if (height > maxHeight) {
                  width = Math.round((width * maxHeight) / height);
                  height = maxHeight;
                }
              }

              canvas.width = width;
              canvas.height = height;

              const ctx = canvas.getContext('2d');
              if (!ctx) {
                clearTimeoutAndReject(new Error('Failed to get canvas context'));
                return;
              }
              
              // Set image smoothing for better quality
              ctx.imageSmoothingEnabled = true;
              ctx.imageSmoothingQuality = 'high';
              ctx.drawImage(img, 0, 0, width, height);

              // Convert to base64 with compression
              // Use JPEG format for better compression (except for PNG files with transparency)
              const outputFormat = file.type.includes('png') ? 'image/png' : 'image/jpeg';
              
              canvas.toBlob(
                (blob) => {
                  if (!blob) {
                    clearTimeoutAndReject(new Error('Failed to compress image to blob'));
                    return;
                  }
                  
                  const blobReader = new FileReader();
                  blobReader.onloadend = () => {
                    if (blobReader.result) {
                      clearTimeoutAndResolve(blobReader.result);
                    } else {
                      clearTimeoutAndReject(new Error('Failed to read compressed image'));
                    }
                  };
                  blobReader.onerror = () => {
                    clearTimeoutAndReject(new Error('Failed to convert blob to base64'));
                  };
                  blobReader.readAsDataURL(blob);
                },
                outputFormat,
                quality
              );
            } catch (error) {
              clearTimeoutAndReject(new Error(`Compression error: ${error.message}`));
            }
          };
          
          // Load the image (no crossOrigin needed for data URLs)
          img.src = event.target.result;
        } catch (error) {
          clearTimeoutAndReject(new Error(`Image processing error: ${error.message}`));
        }
      };
      
      reader.readAsDataURL(file);
    });
  };

  // Image display functions
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      
      setImageFile(file);
      
      // Preview: Convert to base64 for immediate preview
      const reader = new FileReader();
      reader.onloadend = () => {
        // Store file for later upload
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSetImageDisplay = async () => {
    if (!imageFile) {
      toast.error('Please select an image');
      return;
    }
    
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://apidbrapi.arubio.dev';
    
    try {
      console.log('Starting image processing...', {
        fileName: imageFile.name,
        fileSize: imageFile.size,
        fileType: imageFile.type
      });
      
      // Try to compress the image, but fall back to direct conversion if compression fails
      let base64Image;
      try {
        // Use smaller max size for mobile devices
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        const maxSize = isMobile ? 1200 : 1920;
        base64Image = await compressImage(imageFile, maxSize, maxSize, 0.8);
        console.log('Image compressed successfully');
      } catch (compressionError) {
        console.warn('Compression failed, trying direct conversion:', compressionError);
        // Fallback: Convert directly to base64 without compression
        base64Image = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          
          reader.onloadend = () => {
            if (reader.result && reader.result.length > 0) {
              resolve(reader.result);
            } else {
              reject(new Error('FileReader returned empty result'));
            }
          };
          
          reader.onerror = (error) => {
            console.error('FileReader error:', error);
            reject(new Error(`Failed to read file: ${error.message || 'Unknown error'}`));
          };
          
          reader.onabort = () => {
            reject(new Error('File reading was aborted'));
          };
          
          try {
            reader.readAsDataURL(imageFile);
          } catch (err) {
            reject(new Error(`Failed to start reading file: ${err.message}`));
          }
        });
        console.log('Image converted directly (no compression)');
      }
      
      console.log('Uploading image...');
      const response = await fetch(`${backendUrl}/api/image-display/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageData: base64Image,
          text: imageDisplayText.trim() || null
        })
      });
      
      if (response.ok) {
        console.log('Image uploaded successfully');
        setImageFile(null);
        setImageDisplayText('');
        // Clear file input
        const fileInput = document.getElementById('image-upload-input');
        if (fileInput) fileInput.value = '';
        toast.success('Image displayed successfully!');
        // Socket will update via imageDisplayUpdate event
      } else {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { error: errorText || 'Unknown error' };
        }
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }
    } catch (error) {
      console.error('Error setting image display:', error);
      toast.error(`Failed to upload image: ${error.message || 'Please try again'}`);
    }
  };

  const handleHideImageDisplay = async () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://apidbrapi.arubio.dev';
    try {
      const response = await fetch(`${backendUrl}/api/image-display/hide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to hide image' }));
        throw new Error(errorData.error || 'Failed to hide image');
      }
      toast.success('Image hidden successfully!');
      // Socket will update via imageDisplayUpdate event
    } catch (error) {
      console.error('Error hiding image display:', error);
      toast.error(error.message || 'Failed to hide image');
    }
  };
  
  // Animated viewer counts
  const animatedKickViewers = useAnimatedNumber(kickViewers, 2000);
  const animatedPumpViewers = useAnimatedNumber(pumpViewers, 2000);
  const animatedTwitchViewers = useAnimatedNumber(twitchViewers, 2000);

  // Format market cap to display like "$26.8K"
  const formatMarketCap = (value) => {
    if (value === null || value === undefined) return '...';
    
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(1)}K`;
    } else {
      return `$${value.toFixed(0)}`;
    }
  };

  const addMessage = useCallback((newMessage) => {
    setMessages(prev => {
      const updated = [...prev, newMessage];
      // Only trim buffer if user is at bottom (watching live chat)
      // If they're scrolled up reading, let the buffer grow
      const container = chatContainerRef.current;
      if (container) {
        const { scrollTop, scrollHeight, clientHeight } = container;
        const distanceFromBottom = Math.abs(scrollHeight - scrollTop - clientHeight);
        const isAtBottom = distanceFromBottom < 100;
        
        if (isAtBottom && updated.length > 100) {
          return updated.slice(updated.length - 100);
        }
      }
      return updated;
    });
  }, []); // Empty dependency array

  const parseKickEmotes = (content) => {
    // Parse [emote:ID:NAME] format and convert to image elements
    const emoteRegex = /\[emote:(\d+):([^\]]+)\]/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = emoteRegex.exec(content)) !== null) {
      // Add text before the emote
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: content.substring(lastIndex, match.index)
        });
      }
      
      // Add emote
      parts.push({
        type: 'emote',
        id: match[1],
        name: match[2]
      });
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < content.length) {
      parts.push({
        type: 'text',
        content: content.substring(lastIndex)
      });
    }
    
    return parts.length > 0 ? parts : [{ type: 'text', content }];
  };

  const parseUrls = (text) => {
    // URL regex pattern - matches http://, https://, and www. URLs
    // Excludes spaces and common delimiters to avoid matching trailing punctuation
    const urlRegex = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/gi;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = urlRegex.exec(text)) !== null) {
      // Add text before the URL
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: text.substring(lastIndex, match.index)
        });
      }
      
      // Add URL as link
      let url = match[0];
      // Add https:// if URL starts with www.
      if (url.toLowerCase().startsWith('www.')) {
        url = 'https://' + url;
      }
      
      parts.push({
        type: 'link',
        url: url,
        displayText: match[0]
      });
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex)
      });
    }
    
    return parts.length > 0 ? parts : [{ type: 'text', content: text }];
  };

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  const handleScroll = () => {
    if (chatContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
      const isBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 100; // 100px threshold
      setIsAtBottom(isBottom);
      
      // If user scrolled back to bottom, trim the buffer
      if (isBottom) {
        setMessages(prev => {
          if (prev.length > 100) {
            return prev.slice(prev.length - 100);
          }
          return prev;
        });
      }
    }
  };

  const handleDecrease = async () => {
    const amount = parseFloat(inputValue) || 1;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://apidbrapi.arubio.dev';
    try {
      const response = await fetch(`${backendUrl}/api/balance/decrease`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount })
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to decrease balance' }));
        throw new Error(errorData.error || 'Failed to decrease balance');
      }
      const data = await response.json();
      setBalance(data.balance);
      toast.success(
        <div>
          <div>Balance decreased by {`$${amount.toFixed(2)}`}</div>
          <div><strong>New balance: {`$${data.balance.toFixed(2)}`}</strong></div>
        </div>
      );
    } catch (error) {
      console.error('Error decreasing balance:', error);
      toast.error(error.message || 'Failed to decrease balance');
      // Fallback to local update
      setBalance(prev => prev - amount);
    }
    setInputValue('');
  };

  const handleSet = async () => {
    const amount = parseFloat(inputValue) || 0;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://apidbrapi.arubio.dev';
    try {
      const response = await fetch(`${backendUrl}/api/balance/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount })
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to set balance' }));
        throw new Error(errorData.error || 'Failed to set balance');
      }
      const data = await response.json();
      setBalance(data.balance);
      toast.success(`Balance set to $${data.balance.toFixed(2)}`);
    } catch (error) {
      console.error('Error setting balance:', error);
      toast.error(error.message || 'Failed to set balance');
      // Fallback to local update
      setBalance(amount);
    }
    setInputValue('');
  };

  const handleIncrease = async () => {
    const amount = parseFloat(inputValue) || 1;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://apidbrapi.arubio.dev';
    try {
      const response = await fetch(`${backendUrl}/api/balance/increase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount })
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to increase balance' }));
        throw new Error(errorData.error || 'Failed to increase balance');
      }
      const data = await response.json();
      setBalance(data.balance);
      toast.success(
        <div>
          <div>Balance increased by {`$${amount.toFixed(2)}`}</div>
          <div><strong>New balance: {`$${data.balance.toFixed(2)}`}</strong></div>
        </div>
      );
    } catch (error) {
      console.error('Error increasing balance:', error);
      toast.error(error.message || 'Failed to increase balance');
      // Fallback to local update
      setBalance(prev => prev + amount);
    }
    setInputValue('');
  };

  // Challenge management functions
  const fetchChallenges = async () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://apidbrapi.arubio.dev';
    try {
      const response = await fetch(`${backendUrl}/api/challenges`);
      const data = await response.json();
      setChallenges(data.challenges || []);
      setChallengeTriggerAmount(data.triggerAmount || 0);
      setCurrentChallenge(data.currentChallenge || null);
      setTriggerAmountInput(data.triggerAmount ? data.triggerAmount.toString() : '');
    } catch (error) {
      console.error('Error fetching challenges:', error);
    }
  };

  const addChallenge = async () => {
    if (!newChallengeText.trim()) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://apidbrapi.arubio.dev';
    try {
      const response = await fetch(`${backendUrl}/api/challenges/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: newChallengeText.trim() })
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to add challenge' }));
        throw new Error(errorData.error || 'Failed to add challenge');
      }
      const data = await response.json();
      if (data.challenge) {
        setNewChallengeText('');
        toast.success('Challenge added successfully!');
        // Socket will update via challengesUpdate event
      }
    } catch (error) {
      console.error('Error adding challenge:', error);
      toast.error(error.message || 'Failed to add challenge');
    }
  };

  const removeChallenge = async (id) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://apidbrapi.arubio.dev';
    try {
      const response = await fetch(`${backendUrl}/api/challenges/${id}`, {
        method: 'DELETE'
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to remove challenge' }));
        throw new Error(errorData.error || 'Failed to remove challenge');
      }
      toast.success('Challenge removed successfully!');
      // Socket will update via challengesUpdate event
    } catch (error) {
      console.error('Error removing challenge:', error);
      toast.error(error.message || 'Failed to remove challenge');
    }
  };

  const setTriggerAmount = async () => {
    const amount = parseFloat(triggerAmountInput) || 0;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://apidbrapi.arubio.dev';
    try {
      const response = await fetch(`${backendUrl}/api/challenges/trigger-amount`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount })
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to set trigger amount' }));
        throw new Error(errorData.error || 'Failed to set trigger amount');
      }
      toast.success(`Trigger amount set to ${amount} SOL`);
      // Socket will update via challengesUpdate event
    } catch (error) {
      console.error('Error setting trigger amount:', error);
      toast.error(error.message || 'Failed to set trigger amount');
    }
  };

  const completeChallenge = async () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://apidbrapi.arubio.dev';
    try {
      const response = await fetch(`${backendUrl}/api/challenges/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to complete challenge' }));
        throw new Error(errorData.error || 'Failed to complete challenge');
      }
      toast.success('Challenge marked as completed!');
      // Socket will update via challengesUpdate event
    } catch (error) {
      console.error('Error completing challenge:', error);
      toast.error(error.message || 'Failed to complete challenge');
    }
  };

  // TEST FUNCTION: Manually trigger a challenge (for testing purposes)
  const triggerChallengeTest = async () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://apidbrapi.arubio.dev';
    try {
      const response = await fetch(`${backendUrl}/api/challenges/trigger-test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to trigger challenge' }));
        throw new Error(errorData.error || 'Failed to trigger challenge');
      }
      toast.success('Challenge triggered successfully!');
      // Socket will update via challengesUpdate event
    } catch (error) {
      console.error('Error triggering test challenge:', error);
      toast.error(error.message || 'Failed to trigger challenge');
    }
  };

  // TEST FUNCTION: Trigger both buy notification and challenge simultaneously
  const triggerBuyAndChallengeTest = async () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://apidbrapi.arubio.dev';
    try {
      await fetch(`${backendUrl}/api/challenges/trigger-test-with-buy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      // Socket will update via challengesUpdate event and buyTransaction event
    } catch (error) {
      console.error('Error triggering test buy and challenge:', error);
    }
  };


  // Check localStorage for authentication on mount
  useEffect(() => {
    const authStatus = localStorage.getItem('appAuthenticated');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  // Fetch challenges on mount (only if authenticated and not on overlay route)
  useEffect(() => {
    if (currentPath === '/overlay' || !isAuthenticated) {
      return;
    }
    
    const fetchChallengesOnMount = async () => {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://apidbrapi.arubio.dev';
      try {
        const response = await fetch(`${backendUrl}/api/challenges`);
        const data = await response.json();
        setChallenges(data.challenges || []);
        setChallengeTriggerAmount(data.triggerAmount || 0);
        setCurrentChallenge(data.currentChallenge || null);
        setTriggerAmountInput(data.triggerAmount ? data.triggerAmount.toString() : '');
      } catch (error) {
        console.error('Error fetching challenges:', error);
      }
    };
    
    fetchChallengesOnMount();
    
    // Fetch image display state on mount
    const fetchImageDisplay = async () => {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://apidbrapi.arubio.dev';
      try {
        const response = await fetch(`${backendUrl}/api/image-display`);
        const data = await response.json();
        setImageDisplay(data);
      } catch (error) {
        console.error('Error fetching image display:', error);
      }
    };
    
    fetchImageDisplay();
  }, [currentPath, isAuthenticated]);

  // Fetch chat history on mount/refresh
  useEffect(() => {
    // Skip for overlay route or if not authenticated
    if (currentPath === '/overlay' || !isAuthenticated) {
      return;
    }

    const fetchChatHistory = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://apidbrapi.arubio.dev';
        const response = await fetch(`${backendUrl}/api/kick-history`);
        const data = await response.json();
        
        if (data.data && data.data.messages && Array.isArray(data.data.messages)) {
          // Transform API messages to app's message format
          const transformedMessages = data.data.messages
            .filter(msg => (msg.type === 'message' || msg.type === 'reply') && msg.sender && msg.content) // Include messages and replies
            .map(msg => {
              const isReply = msg.type === 'reply';
              let replyMetadata = null;
              
              if (isReply && msg.metadata) {
                try {
                  // Parse metadata if it's a string (from history API)
                  const parsedMetadata = typeof msg.metadata === 'string' 
                    ? JSON.parse(msg.metadata) 
                    : msg.metadata;
                  
                  replyMetadata = {
                    originalSender: parsedMetadata.original_sender?.username,
                    originalSenderColor: parsedMetadata.original_sender?.identity?.color || '#6bff6b',
                    originalMessage: parsedMetadata.original_message?.content
                  };
                } catch (e) {
                  console.error('Error parsing reply metadata:', e);
                }
              }
              
              return {
                platform: 'kick',
                username: msg.sender.username || 'Unknown',
                usernameColor: msg.sender.identity?.color || '#53fc18',
                badges: msg.sender.identity?.badges || [],
                message: msg.content,
                timestamp: new Date(msg.created_at),
                isReply: isReply,
                replyMetadata: replyMetadata
              };
            })
            .reverse(); // Reverse to show oldest first (they come newest first from API)
          
          // Set messages (this will replace any existing messages)
          setMessages(transformedMessages);
          
          // Scroll to bottom after loading history
          setTimeout(() => {
            scrollToBottom();
          }, 100);
        }
      } catch (error) {
        console.error('Error fetching chat history:', error);
      }
    };

    fetchChatHistory();
  }, [currentPath, isAuthenticated]);

  // Preload common badge images to prevent layout shift
  useEffect(() => {
    // Skip preloading for overlay route
    if (currentPath === '/overlay') {
      return;
    }
    
    // List of common badge types on Kick
    const commonBadges = [
      'vip',
      'og',
      'moderator',
      'verified',
      'founder',
      'sub_gifter',
      'subscriber',
      'broadcaster',
      'staff'
    ];
    
    // Preload badge images
    commonBadges.forEach(badgeType => {
      const img = new Image();
      const badgeFilename = badgeMapping[badgeType] || badgeType;
      img.src = `https://assets.kickbotcdn.com/kick_badges/${badgeFilename}.svg`;
    });
  }, [currentPath]);

  // Handle PIN submission
  const handlePinSubmit = (e) => {
    e.preventDefault();
    if (pinInput === CORRECT_PIN) {
      setIsAuthenticated(true);
      localStorage.setItem('appAuthenticated', 'true');
      setPinError('');
      setPinInput('');
    } else {
      setPinError('Incorrect PIN. Please try again.');
      setPinInput('');
    }
  };

  useEffect(() => {
    // Only auto-scroll if user is already at the bottom
    if (isAtBottom) {
      scrollToBottom();
    }
  }, [messages, isAtBottom]);

  useEffect(() => {
    // Skip chat connection for overlay route
    if (currentPath === '/overlay') {
      return;
    }
    
    // Prevent duplicate connections for pump socket only
    if (pumpSocketRef.current) {
      return;
    }

    // Connect to Kick chat
    const connectKick = () => {
      // If already connected, don't reconnect
      if (kickWsRef.current && kickWsRef.current.readyState === WebSocket.OPEN) {
        return; // Already connected
      }
      
      // If already reconnecting, don't start another reconnection
      if (isReconnectingKickRef.current) {
        return;
      }
      
      // Clean up old connection if it exists but is closed
      if (kickWsRef.current) {
        const oldWs = kickWsRef.current;
        // Remove all event handlers to prevent memory leaks
        oldWs.onopen = null;
        oldWs.onmessage = null;
        oldWs.onerror = null;
        oldWs.onclose = null;
        // Close if not already closed
        if (oldWs.readyState !== WebSocket.CLOSED && oldWs.readyState !== WebSocket.CLOSING) {
          oldWs.close();
        }
        kickWsRef.current = null;
      }
      
      // Clear any pending reconnection timeout
      if (reconnectKickTimeoutRef.current) {
        clearTimeout(reconnectKickTimeoutRef.current);
        reconnectKickTimeoutRef.current = null;
      }
      
      const kickWs = new WebSocket('wss://ws-us2.pusher.com/app/32cbd69e4b950bf97679?protocol=7&client=js&version=8.4.0&flash=false');
      kickWsRef.current = kickWs;

      kickWs.onopen = () => {
        console.log('Kick WebSocket connected');
        setKickConnected(true);
        isReconnectingKickRef.current = false; // Reset reconnection flag on successful connection
        
        // Subscribe to channels
        kickWs.send(JSON.stringify({
          event: "pusher:subscribe",
          data: { auth: "", channel: "chatroom_23602260" }
        }));
        kickWs.send(JSON.stringify({
          event: "pusher:subscribe",
          data: { auth: "", channel: "chatrooms.23602260.v2" }
        }));
        kickWs.send(JSON.stringify({
          event: "pusher:subscribe",
          data: { auth: "", channel: "chatrooms.23602260" }
        }));
        // Subscribe to channel subscription events
        kickWs.send(JSON.stringify({
          event: "pusher:subscribe",
          data: { auth: "", channel: "channel.23886990" }
        }));
      };

      kickWs.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Handle chat messages
          if (data.event === "App\\Events\\ChatMessageEvent") {
            const chatData = JSON.parse(data.data);
            if (chatData.sender && chatData.content) {
              // Use the actual created_at timestamp from the message
              const messageTime = chatData.created_at ? new Date(chatData.created_at) : new Date();
              lastKickMessageTimeRef.current = messageTime;
              
              // Check if this is a reply message
              const isReply = chatData.type === 'reply';
              const replyMetadata = isReply && chatData.metadata ? {
                originalSender: chatData.metadata.original_sender?.username,
                originalSenderColor: chatData.metadata.original_sender?.identity?.color || '#6bff6b',
                originalMessage: chatData.metadata.original_message?.content
              } : null;
              
              addMessage({
                platform: 'kick',
                username: chatData.sender.username,
                usernameColor: chatData.sender.identity?.color || '#53fc18',
                badges: chatData.sender.identity?.badges || [],
                message: chatData.content,
                timestamp: messageTime,
                isReply: isReply,
                replyMetadata: replyMetadata
              });
            }
          }
          
          // Handle ban/timeout events
          if (data.event === "App\\Events\\UserBannedEvent") {
            const banData = JSON.parse(data.data);
            const bannedUser = banData.user?.username || 'Unknown';
            const bannedBy = banData.banned_by?.username || 'Moderator';
            const isPermanent = banData.permanent;
            
            let banMessage;
            if (isPermanent) {
              banMessage = `has been banned by ${bannedBy}`;
            } else {
              const duration = banData.duration || 0;
              banMessage = `has been timed out by ${bannedBy} for ${duration} minute${duration !== 1 ? 's' : ''}`;
            }
            
            addMessage({
              platform: 'kick',
              username: bannedUser,
              message: banMessage,
              timestamp: new Date(),
              isBanNotification: true,
              isPermanent: isPermanent
            });
          }
          
          // Handle channel subscription events
          if (data.event === "App\\Events\\ChannelSubscriptionEvent") {
            try {
              const subData = JSON.parse(data.data);
              // Only show single user subscriptions (not multi-user gift subs)
              if (subData.username && subData.username !== "" && subData.user_ids && subData.user_ids.length === 1) {
                console.log(`[SUB] Username: ${subData.username} (ID: ${subData.user_ids[0]}) ChannelID: ${subData.channel_id}`);
                
                addMessage({
                  platform: 'kick',
                  username: subData.username,
                  message: 'has just subscribed!',
                  timestamp: new Date(),
                  isSubscriptionNotification: true
                });
              } else if (subData.user_ids && subData.user_ids.length > 1) {
                // Multi-user gift subscription - this will be handled by LuckyUsersWhoGotGiftSubscriptionsEvent
                console.log(`[GIFT] User ID(s): ${subData.user_ids} ChannelID: ${subData.channel_id}`);
              }
            } catch (error) {
              console.error('Error parsing subscription event:', error);
            }
          }
          
          // Handle gifted subscription events
          if (data.event === "App\\Events\\LuckyUsersWhoGotGiftSubscriptionsEvent") {
            try {
              const giftData = JSON.parse(data.data);
              if (giftData.gifter_username && giftData.usernames && giftData.usernames.length > 0) {
                console.log(`${giftData.gifter_username} has gifted ${giftData.usernames.length} subs to ${giftData.usernames.join(', ')}!`);
                
                addMessage({
                  platform: 'kick',
                  username: giftData.gifter_username,
                  message: `has gifted ${giftData.usernames.length} sub${giftData.usernames.length !== 1 ? 's' : ''}!`,
                  timestamp: new Date(),
                  isGiftSubscriptionNotification: true
                });
              }
            } catch (error) {
              console.error('Error parsing gift subscription event:', error);
            }
          }
        } catch (error) {
          console.error('Error parsing Kick message:', error);
        }
      };

      kickWs.onerror = (error) => {
        console.error('Kick WebSocket error:', error);
      };

      kickWs.onclose = (event) => {
        console.log('Kick WebSocket closed', event.code, event.reason);
        setKickConnected(false);
        
        // Don't reconnect if already reconnecting
        if (isReconnectingKickRef.current) {
          return;
        }
        
        // Don't reconnect if this was a manual close (code 1000 = normal closure)
        if (event.code === 1000) {
          console.log('WebSocket closed normally, not reconnecting');
          return;
        }
        
        // Mark as reconnecting to prevent multiple reconnection attempts
        isReconnectingKickRef.current = true;
        
        // Reconnect quickly (1 second) to minimize message loss
        // A quick reconnect might allow the server to maintain session continuity
        reconnectKickTimeoutRef.current = setTimeout(() => {
          isReconnectingKickRef.current = false;
          connectKick();
        }, 1000);
      };
    };

    // Connect to pump.fun chat via backend proxy
    const connectPump = () => {
      if (pumpSocketRef.current && pumpSocketRef.current.connected) {
        return; // Already connected
      }
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://apidbrapi.arubio.dev';
      const socket = io(backendUrl, {
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 1000, // Faster reconnection to minimize message loss
        reconnectionDelayMax: 5000, // Max delay for exponential backoff
        reconnectionAttempts: Infinity, // Unlimited reconnection attempts
        timeout: 20000 // Connection timeout
      });
      
      pumpSocketRef.current = socket;

      socket.on('connect', () => {
        console.log('Connected to backend proxy server');
        setPumpConnected(true);
        setTwitchConnected(true); // Twitch is also handled by backend
        // Request current balance on connect
        socket.emit('getBalance');
      });

      socket.on('balanceUpdate', (newBalance) => {
        console.log('Balance update received:', newBalance);
        setBalance(newBalance);
      });

      socket.on('challengesUpdate', (challengeData) => {
        console.log('Challenges update received:', challengeData);
        setChallenges(challengeData.challenges || []);
        setChallengeTriggerAmount(challengeData.triggerAmount || 0);
        setCurrentChallenge(challengeData.currentChallenge || null);
      });

      socket.on('challengeTriggered', (data) => {
        console.log('Challenge triggered:', data);
        // The challengeUpdate event will also fire, but this is a notification
      });

      socket.on('imageDisplayUpdate', (data) => {
        console.log('Image display update received:', data);
        setImageDisplay(data);
        // Reset warning dismissal when image visibility changes
        if (data.visible !== imageDisplay.visible) {
          setImageDisplayWarningDismissed(false);
        }
      });

      socket.on('pumpMessage', (msgData) => {
        console.log('Pump.fun message from proxy:', msgData);
        if (msgData.username && msgData.message) {
          // For buy notifications, keep full wallet address, otherwise truncate
          const displayUsername = msgData.isBuyNotification 
            ? msgData.username // Full wallet for buy notifications
            : (msgData.username.length > 20 
                ? msgData.username.substring(0, 6) + '...'
                : msgData.username);
          
          addMessage({
            platform: 'pump',
            username: displayUsername,
            message: msgData.message,
            timestamp: msgData.timestamp || new Date(),
            isBuyNotification: msgData.isBuyNotification || false
          });
        }
      });

      socket.on('twitchMessage', (msgData) => {
        console.log('Twitch message from proxy:', msgData);
        if (msgData.username && msgData.message) {
          addMessage({
            platform: 'twitch',
            username: msgData.username,
            usernameColor: msgData.usernameColor || '#9146FF',
            badges: msgData.badges || [],
            message: msgData.message,
            timestamp: msgData.timestamp ? new Date(msgData.timestamp) : new Date(),
            userId: msgData.userId,
            isMod: msgData.isMod || false,
            isSubscriber: msgData.isSubscriber || false,
            isVip: msgData.isVip || false,
            isBroadcaster: msgData.isBroadcaster || false
          });
        }
      });

      socket.on('disconnect', (reason) => {
        console.log('Disconnected from backend proxy:', reason);
        setPumpConnected(false);
        setTwitchConnected(false);
      });

      socket.on('connect_error', (error) => {
        console.error('Backend proxy connection error:', error);
      });
    };

    connectKick();
    connectPump();

    // Cleanup on unmount
    return () => {
      // Clear reconnection timeout
      if (reconnectKickTimeoutRef.current) {
        clearTimeout(reconnectKickTimeoutRef.current);
        reconnectKickTimeoutRef.current = null;
      }
      
      // Mark as not reconnecting to prevent reconnection after unmount
      isReconnectingKickRef.current = false;
      
      if (kickWsRef.current) {
        // Remove event handlers
        kickWsRef.current.onopen = null;
        kickWsRef.current.onmessage = null;
        kickWsRef.current.onerror = null;
        kickWsRef.current.onclose = null;
        // Close connection
        if (kickWsRef.current.readyState !== WebSocket.CLOSED && kickWsRef.current.readyState !== WebSocket.CLOSING) {
          kickWsRef.current.close();
        }
        kickWsRef.current = null;
      }
      if (pumpSocketRef.current) {
        pumpSocketRef.current.disconnect();
        pumpSocketRef.current = null;
      }
    };
  }, [currentPath, addMessage]); // Includes dependencies

  // Fetch viewer counts (every 1 minute)
  useEffect(() => {
    // Skip for overlay route
    if (currentPath === '/overlay') {
      return;
    }

    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://apidbrapi.arubio.dev';

    const fetchViewerCounts = async () => {
      // Fetch Kick viewers directly
      try {
        const kickResponse = await fetch('https://kick.com/api/v1/channels/dbr666/');
        const kickData = await kickResponse.json();
        if (kickData.livestream && kickData.livestream.viewer_count !== undefined) {
          setKickViewers(kickData.livestream.viewer_count);
        }
      } catch (error) {
        console.error('Error fetching Kick viewers:', error);
      }

      // Fetch Pump data (viewers + market cap) via backend proxy (CORS issue)
      try {
        const pumpResponse = await fetch(`${backendUrl}/api/pump-data`);
        const pumpData = await pumpResponse.json();
        if (pumpData.numParticipants !== undefined) {
          setPumpViewers(pumpData.numParticipants);
        }
        if (pumpData.usd_market_cap !== undefined) {
          setMarketCap(pumpData.usd_market_cap);
        }
      } catch (error) {
        console.error('Error fetching Pump data:', error);
      }

      // Fetch Twitch viewers via backend proxy
      try {
        const twitchResponse = await fetch(`${backendUrl}/api/twitch-viewers`);
        const twitchData = await twitchResponse.json();
        if (twitchData.viewerCount !== undefined) {
          setTwitchViewers(twitchData.viewerCount);
        }
      } catch (error) {
        console.error('Error fetching Twitch viewers:', error);
      }
    };

    // Fetch immediately on mount
    fetchViewerCounts();

    // Then fetch every 60 seconds
    const viewerInterval = setInterval(fetchViewerCounts, 60000);

    return () => clearInterval(viewerInterval);
  }, [currentPath]);

  // Fetch bitrate (every 1 second) - COMMENTED OUT: Not fetching bitrate anymore
  // This effect fetches the streaming bitrate from the SRT stats API
  // The bitrate represents the current streaming quality/bandwidth in bits per second
  /*
  useEffect(() => {
    // Skip for overlay route
    if (currentPath === '/overlay') {
      return;
    }

    // Async function to fetch bitrate data from SRT stats endpoint
    const fetchBitrate = async () => {
      try {
        // Fetch bitrate statistics from belabox SRT stats API
        const response = await fetch('https://stats.srt.belabox.net/ELUN2c2h8lTmM7ypkI2cvZNOXiv7w5');
        const data = await response.json();
        
        // Extract bitrate value from nested API response structure
        // The bitrate is located at data.publishers.live.bitrate
        if (data.publishers && data.publishers.live && data.publishers.live.bitrate !== undefined) {
          setBitrate(data.publishers.live.bitrate);
        }
      } catch (error) {
        console.error('Error fetching bitrate:', error);
      }
    };

    // Fetch immediately on mount to get initial bitrate value
    fetchBitrate();

    // Then fetch every 1 second to keep bitrate updated in real-time
    const bitrateInterval = setInterval(fetchBitrate, 1000);

    // Cleanup: clear interval when component unmounts or path changes
    return () => clearInterval(bitrateInterval);
  }, [currentPath]);
  */

  // If on /overlay route, show overlay component
  if (currentPath === '/overlay') {
    return <Overlay />;
  }

  // Show PIN screen if not authenticated (main app only)
  if (!isAuthenticated) {
    return (
      <div className="App">
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="dark"
        />
        <div className="pin-container">
          <div className="pin-box">
            <h2>Enter PIN</h2>
            <form onSubmit={handlePinSubmit}>
              <input
                type="password"
                className="pin-input"
                value={pinInput}
                onChange={(e) => setPinInput(e.target.value)}
                placeholder="Enter PIN"
                autoFocus
                maxLength="4"
              />
              {pinError && <div className="pin-error">{pinError}</div>}
              <button type="submit" className="pin-submit">Submit</button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Main chat interface
  return (
    <div className="App">
      <div className="balance-container">
        <div className="balance-display">Balance: ${balance.toFixed(2)}</div>
        <input
          type="number"
          className="balance-input"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder="Enter amount"
        />
        <div className="balance-buttons">
          <button onClick={handleDecrease} disabled={!inputValue || !(kickConnected && pumpConnected && twitchConnected)}>Decrease</button>
          <button onClick={handleIncrease} disabled={!inputValue || !(kickConnected && pumpConnected && twitchConnected)}>Increase</button>
          <button onClick={handleSet} disabled={!inputValue || !(kickConnected && pumpConnected && twitchConnected)}>Set</button>
          <button onClick={() => {
            setShowChallengesModal(true);
            setImageDisplayWarningDismissed(false); // Reset warning when opening modal
          }} disabled={!(kickConnected && pumpConnected && twitchConnected)} className="challenges-button">Challenges</button>
        </div>
        
        {/* Stats display - compact with connection status */}
        <div className="stats-compact">
          <span className="stat-kick">{animatedKickViewers.toLocaleString()}</span>
          <span className="stat-pump">{animatedPumpViewers.toLocaleString()}</span>
          {/* UNCOMMENT THIS WHEN YOU WANT TO SHOW TWITCH VIEWERS */}
          <span className="stat-twitch">{animatedTwitchViewers.toLocaleString()}</span>
          <span className="stat-marketcap">{formatMarketCap(marketCap)}</span>
          {/* Connection status indicator - inline */}
          <span className={`connection-status ${kickConnected && pumpConnected && twitchConnected ? 'connected' : 'reconnecting'}`}>
            {kickConnected && pumpConnected && twitchConnected ? 'Connected' : 'Reconnecting'}
          </span>
          {/* Bitrate display - commented out for now, showing only viewers */}
          {/* <span className="stat-bitrate">{bitrate !== null ? bitrate.toLocaleString() : '...'}</span> */}
        </div>
        
        {/* Current Challenge Display (below stats) */}
        {currentChallenge && !showChallengesModal && (
          <div className="current-challenge-banner">
            <div className="current-challenge-banner-content">
              <span className="current-challenge-banner-label">Challenge:</span>
              <span className="current-challenge-banner-text">{currentChallenge.text}</span>
              <button onClick={completeChallenge} className="complete-challenge-banner-btn" disabled={!(kickConnected && pumpConnected && twitchConnected)}>✓</button>
            </div>
          </div>
        )}

      </div>
      
      <div className="chat-container">
        <div className="chat-messages" ref={chatContainerRef} onScroll={handleScroll}>
          {messages.map((msg, index) => {
            const messageParts = msg.platform === 'kick' ? parseKickEmotes(msg.message) : [{ type: 'text', content: msg.message }];
            const replyMessageParts = msg.isReply && msg.replyMetadata ? parseKickEmotes(msg.replyMetadata.originalMessage) : [];
            
            return (
              <div key={index} className={`chat-message ${msg.platform} ${msg.isBuyNotification ? 'buy-notification-message' : ''} ${msg.isBanNotification ? 'ban-notification-message' : ''} ${msg.isSubscriptionNotification ? 'subscription-notification-message' : ''} ${msg.isGiftSubscriptionNotification ? 'gift-subscription-notification-message' : ''} ${msg.isReply ? 'reply-message' : ''}`}>
                {msg.isReply && msg.replyMetadata && (
                  <div className="reply-reference">
                    <svg className="reply-icon" viewBox="0 0 24 24" fill="currentColor" width="12" height="12">
                      <path d="M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z"/>
                    </svg>
                    <span className="reply-original-sender" style={{ color: msg.replyMetadata.originalSenderColor }}>@{msg.replyMetadata.originalSender}</span>
                    <span className="reply-original-message">
                      {replyMessageParts.map((part, partIndex) => {
                        if (part.type === 'emote') {
                          return (
                            <img
                              key={partIndex}
                              src={`https://files.kick.com/emotes/${part.id}/fullsize`}
                              alt={part.name}
                              className="reply-emote"
                              title={part.name}
                              height="16"
                              loading="eager"
                            />
                          );
                        } else if (part.type === 'text') {
                          // Parse URLs in text parts
                          const urlParts = parseUrls(part.content);
                          return (
                            <span key={partIndex}>
                              {urlParts.map((urlPart, urlPartIndex) => {
                                if (urlPart.type === 'link') {
                                  return (
                                    <a
                                      key={urlPartIndex}
                                      href={urlPart.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="message-link"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {urlPart.displayText}
                                    </a>
                                  );
                                } else {
                                  return <span key={urlPartIndex}>{urlPart.content}</span>;
                                }
                              })}
                            </span>
                          );
                        } else {
                          return <span key={partIndex}>{part.content}</span>;
                        }
                      })}
                    </span>
                  </div>
                )}
                <span className={`platform-badge ${msg.isBuyNotification ? 'buy' : msg.platform}`}>
                  {msg.isBuyNotification ? 'P' : msg.isBanNotification ? 'K' : (msg.platform === 'kick' ? 'K' : msg.platform === 'twitch' ? 'T' : 'P')}
                </span>
                {msg.badges && msg.badges.length > 0 && (
                  <span className="user-badges">
                    {msg.badges.map((badge, badgeIndex) => {
                      let badgeImageUrl;
                      
                      // Handle Twitch badges differently from Kick badges
                      if (msg.platform === 'twitch') {
                        // Twitch badges use UUIDs, not badge type names
                        const version = parseInt(badge.version || '1', 10);
                        badgeImageUrl = getTwitchBadgeUrl(badge.type, version);
                        
                        // Skip unknown badges
                        if (!badgeImageUrl) {
                          console.warn(`Unknown Twitch badge type: ${badge.type}, version: ${version}`);
                          return null;
                        }
                      } else {
                        // Kick badges: Use kickbotcdn.com badge URLs
                        const badgeFilename = badgeMapping[badge.type] || badge.type;
                        badgeImageUrl = `https://assets.kickbotcdn.com/kick_badges/${badgeFilename}.svg`;
                      }
                      
                      return (
                        <img
                          key={badgeIndex}
                          src={badgeImageUrl}
                          alt={badge.type}
                          className="badge-image"
                          title={badge.type}
                          width="18"
                          height="18"
                          loading="eager"
                          onError={(e) => {
                            // Hide the image if it fails to load
                            e.target.style.display = 'none';
                          }}
                        />
                      );
                    }).filter(Boolean)}
                  </span>
                )}
                <span className="username" style={{ color: msg.isBanNotification ? '#6bff6b' : (msg.isSubscriptionNotification || msg.isGiftSubscriptionNotification) ? '#53fc18' : (msg.usernameColor || (msg.platform === 'kick' ? '#53fc18' : msg.platform === 'twitch' ? '#9146FF' : '#6bb6ff')) }}>
                  {msg.username}{msg.isBuyNotification || msg.isBanNotification || msg.isSubscriptionNotification || msg.isGiftSubscriptionNotification ? '' : ':'}
                </span>{' '}
                <span className="message">
                  {messageParts.map((part, partIndex) => {
                    if (part.type === 'emote') {
                      return (
                        <img
                          key={partIndex}
                          src={`https://files.kick.com/emotes/${part.id}/fullsize`}
                          alt={part.name}
                          className="emote"
                          title={part.name}
                          height="30"
                          loading="eager"
                        />
                      );
                    } else if (part.type === 'text') {
                      // Parse URLs in text parts
                      const urlParts = parseUrls(part.content);
                      return (
                        <span key={partIndex}>
                          {urlParts.map((urlPart, urlPartIndex) => {
                            if (urlPart.type === 'link') {
                              return (
                                <a
                                  key={urlPartIndex}
                                  href={urlPart.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="message-link"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {urlPart.displayText}
                                </a>
                              );
                            } else {
                              return <span key={urlPartIndex}>{urlPart.content}</span>;
                            }
                          })}
                        </span>
                      );
                    } else {
                      return <span key={partIndex}>{part.content}</span>;
                    }
                  })}
                </span>
              </div>
            );
          })}
          <div ref={chatEndRef} />
        </div>
      </div>

      {/* Challenges Modal */}
      {showChallengesModal && (
        <div className="modal-overlay" onClick={() => setShowChallengesModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Challenges</h2>
              <button className="modal-close" onClick={() => setShowChallengesModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              {/* Image Display Warning */}
              {imageDisplay.visible && imageDisplay.imageData && (
                <div className="image-display-modal-warning">
                  <div className="image-display-modal-warning-content">
                    <span className="image-display-modal-warning-icon">⚠️</span>
                    <span className="image-display-modal-warning-text">An image is currently being displayed on the stream. Don't forget to hide it when you're done.</span>
                  </div>
                </div>
              )}

              {/* Manual Challenge Trigger Button */}
              <div className="test-trigger-section">
                <button 
                  onClick={triggerChallengeTest} 
                  className="test-trigger-btn"
                  disabled={challenges.length === 0}
                >
                  Trigger Random Challenge
                </button>
              </div>

              {/* Current Challenge Display */}
              {currentChallenge && (
                <div className="current-challenge-section">
                  <h3>Current Challenge</h3>
                  <div className="current-challenge-box">
                    <p>{currentChallenge.text}</p>
                    <button onClick={completeChallenge} className="complete-challenge-btn" disabled={!(kickConnected && pumpConnected && twitchConnected)}>Mark as Completed</button>
                  </div>
                </div>
              )}

              {/* Trigger Amount Setting */}
              <div className="trigger-amount-section">
                <h3>Trigger Amount (SOL)</h3>
                <div className="trigger-input-group">
                  <input
                    type="number"
                    className="trigger-input"
                    value={triggerAmountInput}
                    onChange={(e) => setTriggerAmountInput(e.target.value)}
                    placeholder="Enter SOL amount"
                    step="0.1"
                    min="0"
                  />
                  <button onClick={setTriggerAmount} className="set-trigger-btn">Set Trigger</button>
                </div>
                <p className="trigger-info">Challenge will trigger when a single buy reaches or exceeds {challengeTriggerAmount} SOL</p>
              </div>

              {/* Challenge List */}
              <div className="challenges-list-section">
                <h3>All Challenges ({challenges.length})</h3>
                <div className="challenges-list">
                  {challenges.length === 0 ? (
                    <p className="no-challenges">No challenges yet. Add one below!</p>
                  ) : (
                    challenges.map((challenge) => (
                      <div key={challenge.id} className="challenge-item">
                        <span className="challenge-text">{challenge.text}</span>
                        <button
                          onClick={() => removeChallenge(challenge.id)}
                          className="remove-challenge-btn"
                        >
                          Remove
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Add Challenge */}
              <div className="add-challenge-section">
                <h3>Add New Challenge</h3>
                <div className="add-challenge-group">
                  <input
                    type="text"
                    className="add-challenge-input"
                    value={newChallengeText}
                    onChange={(e) => setNewChallengeText(e.target.value)}
                    placeholder="Enter challenge text"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        addChallenge();
                      }
                    }}
                  />
                  <button onClick={addChallenge} className="add-challenge-btn" disabled={!newChallengeText.trim()}>
                    Add Challenge
                  </button>
                </div>
              </div>

              {/* Image Display Section */}
              <div className="image-display-section">
                <h3>Stream Image Display</h3>
                <div className="image-display-group">
                  <input
                    id="image-upload-input"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="image-upload-input"
                  />
                  <input
                    type="text"
                    className="image-display-text-input"
                    value={imageDisplayText}
                    onChange={(e) => setImageDisplayText(e.target.value)}
                    placeholder="Optional text (e.g., table 69)"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleSetImageDisplay();
                      }
                    }}
                  />
                  <div className="image-display-buttons">
                    <button 
                      onClick={handleSetImageDisplay} 
                      className="set-image-display-btn" 
                      disabled={!imageFile}
                    >
                      Display Image
                    </button>
                    <button 
                      onClick={handleHideImageDisplay} 
                      className="hide-image-display-btn"
                      disabled={!imageDisplay.visible}
                    >
                      Hide Image
                    </button>
                  </div>
                  {imageFile && (
                    <div className="image-preview">
                      <span className="image-preview-label">Preview:</span>
                      <div className="overlay-image-wrapper">
                        {imageDisplayText && (
                          <div className="overlay-image-display-text">{imageDisplayText}</div>
                        )}
                        <img src={URL.createObjectURL(imageFile)} alt="Preview" className="overlay-image-display-img" />
                      </div>
                    </div>
                  )}
                  {/* Currently Displayed Image Preview */}
                  {imageDisplay.visible && imageDisplay.imageData && (
                    <div className="image-preview">
                      <span className="image-preview-label">Currently Displayed:</span>
                      <div className="overlay-image-wrapper">
                        {imageDisplay.text && (
                          <div className="overlay-image-display-text">{imageDisplay.text}</div>
                        )}
                        <img src={imageDisplay.imageData} alt="Currently displayed" className="overlay-image-display-img" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
    </div>
  );
}

export default App;
